import clsx from 'clsx';
import BubbleComponent from 'core/modules/atoms/Bubble';
import Icon from 'ui/atoms/Icon/Icon';
import type { IconName } from 'ui/icons/types';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import React from 'react';
import { Link } from 'react-router-dom';
import useHeaderTabs, { type Tab } from './useHeaderTabs';

const Bubble = BubbleComponent as unknown as React.ComponentType<Record<string, unknown>>;

type HeaderTabsProps = {
  readonly tabs?: Tab[];
  readonly tabActive?: string;
  readonly onChangeTab?: (id: string) => void;
  readonly onDisableTab?: (id: string) => void;
  /**
   * Variante visual del tablist.
   * NOTA: 'border' está en desuso — migrar a SimpleTab. 'pill' sigue para pestañas que conmutan
   * contenido (tablist, como `HeaderWithTabs`); como navegación, SimpleTab.
   * (Sin anotación JSDoc de obsolescencia hasta que exista el reemplazo, para no generar avisos
   * inaccionables. No escribir aquí esa etiqueta: los analizadores la leen aunque vaya en medio de
   * una frase y marcan la prop entera como obsoleta.)
   */
  readonly typeView?: 'default' | 'border' | 'background' | 'background-round' | 'vertical' | 'pill';
  readonly align?: 'default' | 'left' | 'right';
  /**
   * Semántica del contenedor. `'tablist'` (por defecto) conmuta contenido dentro de la misma vista;
   * `'navigation'` renderiza `<nav>` + `aria-current="page"` para pestañas que cambian de URL.
   */
  readonly as?: 'tablist' | 'navigation';
  /**
   * ID del panel (`role="tabpanel"`) que controlan las pestañas. Cuando se pasa, cada pestaña
   * renderiza `aria-controls={panelId}`, completando la referencia bidireccional del patrón ARIA
   * tabs (el panel debe apuntar de vuelta con `aria-labelledby`). Si se omite, no se renderiza
   * `aria-controls`. Solo aplica en modo `'tablist'`.
   */
  readonly panelId?: string;
  readonly label?: string;
};

type HeaderTabItemProps = {
  readonly handleKeyDown: (event: React.KeyboardEvent<HTMLElement>, index: number) => void;
  readonly index: number;
  readonly isActive: boolean;
  readonly isInTabSequence: boolean;
  readonly isNavigation: boolean;
  readonly onClickChangeTab: (tab: Tab) => void;
  readonly panelId?: string;
  readonly registerTabRef: (index: number) => (el: HTMLElement | null) => void;
  readonly tab: Tab;
};

export function tabElementId(id: string): string {
  return `tab-${id}`;
}

export function panelElementId(id: string): string {
  return `panel-${id}`;
}

type TabAriaParams = Pick<HeaderTabItemProps, 'isActive' | 'isInTabSequence' | 'isNavigation' | 'panelId' | 'tab'>;

function getTabAriaProps({ isActive, isInTabSequence, isNavigation, panelId, tab }: TabAriaParams): React.HTMLAttributes<HTMLElement> {
  const ariaDisabled = tab.disabled ? true : undefined;

  if (isNavigation) {
    return {
      'aria-current': isActive ? 'page' : undefined,
      'aria-disabled': ariaDisabled,
      tabIndex: tab.disabled ? -1 : 0,
    };
  }

  return {
    role: 'tab',
    id: tabElementId(tab.id),
    'aria-selected': isActive,
    'aria-controls': panelId,
    'aria-disabled': ariaDisabled,
    tabIndex: isInTabSequence ? 0 : -1,
  };
}

function HeaderTabItem({
  handleKeyDown,
  index,
  isActive,
  isInTabSequence,
  isNavigation,
  onClickChangeTab,
  panelId,
  registerTabRef,
  tab,
}: HeaderTabItemProps): JSX.Element {
  const className = clsx('header-tabs__tab', `header-tabs__tab--id-${tab.id}`, {
    'header-tabs__tab--active': isActive,
    'header-tabs__tab--with-icon': tab.icon,
    'header-tabs__tab--disabled': tab.disabled,
  });

  const sharedAriaProps = getTabAriaProps({ isActive, isInTabSequence, isNavigation, panelId, tab });

  // A disabled option cannot be a link: it must stay clickable to report the "coming soon" case.
  const navigationHref = isNavigation && !tab.disabled ? tab.to : undefined;

  function handleClick(): void {
    onClickChangeTab(tab);
  }

  function handleKey(event: React.KeyboardEvent<HTMLElement>): void {
    handleKeyDown(event, index);
  }

  const content = (
    <div className="header-tabs__tab-text-container">
      {tab.bubble && (
        <div className="header-tabs__bubble">
          <Bubble />
        </div>
      )}
      {tab.icon && (
        <div className="header-tabs__tab-icon">
          <Icon name={tab.icon as IconName} color={isActive ? 'primary' : 'default'} />
        </div>
      )}
      <span className="header-tabs__tab-text">{tab.name}</span>
    </div>
  );

  const tabElement = navigationHref ? (
    <Link to={navigationHref} ref={registerTabRef(index)} className={className} onClick={handleClick} onKeyDown={handleKey} {...sharedAriaProps}>
      {content}
    </Link>
  ) : (
    <button type="button" ref={registerTabRef(index)} className={className} onClick={handleClick} onKeyDown={handleKey} {...sharedAriaProps}>
      {content}
    </button>
  );

  if (tab.tooltip) {
    return (
      <Tooltip placement="right" title={tab.tooltip}>
        {tabElement}
      </Tooltip>
    );
  }

  return tabElement;
}

function HeaderTabs({
  tabs = [],
  tabActive,
  onChangeTab,
  onDisableTab = () => {},
  typeView = 'default',
  align = 'default',
  as = 'tablist',
  panelId,
  label,
}: HeaderTabsProps): JSX.Element {
  const isNavigation = as === 'navigation';
  const { activeTabState, rovingTabIndex, registerTabRef, onClickChangeTab, handleKeyDown } = useHeaderTabs({
    tabs,
    tabActive,
    onChangeTab,
    onDisableTab,
    isNavigation,
  });

  const items = tabs.map((tab, index) => (
    <HeaderTabItem
      key={tab.id}
      handleKeyDown={handleKeyDown}
      index={index}
      isActive={tab.id === activeTabState}
      isInTabSequence={index === rovingTabIndex}
      isNavigation={isNavigation}
      onClickChangeTab={onClickChangeTab}
      panelId={panelId}
      registerTabRef={registerTabRef}
      tab={tab}
    />
  ));

  return (
    <div className={clsx('header-tabs', `header-tabs__type-view-${typeView}`, `header-tabs__align-${align}`)}>
      {isNavigation ? (
        <nav className="header-tabs__list" aria-label={label}>
          {items}
        </nav>
      ) : (
        <div className="header-tabs__list" role="tablist" aria-orientation={typeView === 'vertical' ? 'vertical' : 'horizontal'} aria-label={label}>
          {items}
        </div>
      )}
    </div>
  );
}

export default HeaderTabs;
